import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff } from "lucide-react";
import { API } from "../lib/api";

export default function HostLoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [propertyName, setPropertyName] = useState("");
  const [location, setLocation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin 
        ? { email, password }
        : { email, password, property_name: propertyName, location };

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      localStorage.setItem("ospitai-host-token", response.data.token);
      localStorage.setItem("ospitai-host", JSON.stringify(response.data.host));
      
      navigate("/host-dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6 py-12" data-testid="host-login-page">
      <div className="max-w-md w-full bg-white rounded-2xl p-8 shadow-sm border border-stone-100">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-teal-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-2xl">O</span>
          </div>
          <h1 className="text-2xl font-serif text-stone-900">{isLogin ? "Host Login" : "Create Account"}</h1>
          <p className="text-stone-500 text-sm mt-2">
            {isLogin ? "Access your OspitAI dashboard" : "Register your property with OspitAI"}
          </p>
        </div>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Email</label>
            <input 
              type="email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
              placeholder="your@email.com"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Password</label>
            <div className="relative">
              <input 
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="w-full border border-stone-200 rounded-xl px-4 py-3 pr-12 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {!isLogin && (
            <>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Property Name</label>
                <input 
                  type="text" 
                  value={propertyName}
                  onChange={(e) => setPropertyName(e.target.value)}
                  required
                  className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
                  placeholder="Casa Limone"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Location</label>
                <input 
                  type="text" 
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  required
                  className="w-full border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
                  placeholder="Monopoli, Puglia"
                />
              </div>
            </>
          )}

          <button 
            type="submit"
            disabled={loading}
            className="w-full bg-teal-700 hover:bg-teal-800 disabled:bg-stone-300 text-white py-3 rounded-xl font-medium transition-colors"
          >
            {loading ? "Please wait..." : (isLogin ? "Sign in" : "Create account")}
          </button>
        </form>
        
        <p className="text-center text-sm text-stone-500 mt-6">
          {isLogin ? "Don't have an account? " : "Already have an account? "}
          <button 
            onClick={() => { setIsLogin(!isLogin); setError(""); }}
            className="text-teal-700 font-medium hover:underline"
          >
            {isLogin ? "Register" : "Sign in"}
          </button>
        </p>

        <div className="mt-6 pt-6 border-t border-stone-100 text-center">
          <p className="text-xs text-stone-400">
            By signing in, you agree to our{" "}
            <Link to="/terms" className="text-teal-600 hover:underline">Terms</Link>
            {" "}and{" "}
            <Link to="/privacy" className="text-teal-600 hover:underline">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
